// Web view model for canonical nakshatra facts plus presentation-only copy.

import { nakshatras, type Nakshatra, type NakshatraId } from "@ishvaram/domain-data/nakshatras";
import { planetMap, planets, type Planet } from "@ishvaram/domain-data/planets";
import { rashiMap, rashis } from "@ishvaram/domain-data/rashis";

import { significationLabels } from "./nakshatra-signification-labels";

import {
  NAKSHATRA_PAGE_PRESENTATION,
  type NakshatraPagePresentation,
} from "./nakshatra-page-presentation-data";

export interface NakshatraData {
  id: NakshatraId;
  name_en: string;
  name_hi: string;
  number: number;
  degree_start: number;
  degree_end: number;
  rashi: string;
  rashi_en: string;
  ruler: string;
  deity: string;
  symbol: string;
  nature: string;
  gana: string;
  quality: string;
  signifies: string[];
  seo_text: string;
  padas: { navamsha: string; traits: string }[];
}

export interface PlanetData {
  id: string;
  name_en: string;
  name_hi: string;
  signifies: string[];
  gemstone: string;
  day: string;
  color: string;
}

const NATURE_LABELS: Readonly<Record<string, string>> = {
  deva: "Deva (Divine)",
  manushya: "Manushya (Human)",
  rakshasa: "Rakshasa (Fierce)",
};

const QUALITY_LABELS: Readonly<Record<string, string>> = {
  swift: "Swift (Kshipra)",
  fierce: "Fierce (Ugra)",
  mixed: "Mixed (Mishra)",
  fixed: "Fixed (Dhruva)",
  soft: "Soft (Mridu)",
  sharp: "Sharp (Tikshna)",
  movable: "Movable (Chara)",
  light: "Light (Laghu)",
};

/** Reader label for a canonical nakshatra quality id (e.g. `movable` → "Movable (Chara)"). */
export function nakshatraQualityLabel(value: string): string {
  return QUALITY_LABELS[value] ?? titleCase(value);
}

/** Reader label for a canonical nakshatra nature id (e.g. `rakshasa` → "Rakshasa (Fierce)"). */
export function nakshatraNatureLabel(value: string): string {
  return NATURE_LABELS[value] ?? titleCase(value);
}

function titleCase(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
}

function normalizeVedicName(value: string): string {
  return value.toLowerCase().replace(/a$/, "");
}

const rashiByVedicName = new Map(rashis.map((rashi) => [normalizeVedicName(rashi.name_sa), rashi]));

function requirePresentation(id: NakshatraId): NakshatraPagePresentation {
  const presentation = NAKSHATRA_PAGE_PRESENTATION[id];
  if (!presentation || presentation.id !== id) {
    throw new Error(`Missing nakshatra page presentation for canonical id "${id}"`);
  }
  return presentation;
}

function requireLabel(labels: Readonly<Record<string, string>>, value: string): string {
  const label = labels[value];
  if (!label) throw new Error(`Missing presentation label for canonical value "${value}"`);
  return label;
}

function toPlanetData(planet: Planet): PlanetData {
  return {
    id: planet.id,
    name_en: planet.name_en,
    name_hi: planet.name_hi,
    signifies: significationLabels(planet.signifies),
    gemstone: titleCase(planet.gemstone),
    day: titleCase(planet.day),
    color: titleCase(planet.color),
  };
}

function toNakshatraData(canonical: Nakshatra): NakshatraData {
  const presentation = requirePresentation(canonical.id);
  const rashi = rashiMap[canonical.rashi];
  const ruler = planetMap[canonical.ruler];
  if (!rashi) throw new Error(`Unknown rashi "${canonical.rashi}" for ${canonical.id}`);
  if (!ruler) throw new Error(`Unknown ruler "${canonical.ruler}" for ${canonical.id}`);
  if (!canonical.padas || canonical.padas.length !== 4) {
    throw new Error(`Canonical nakshatra "${canonical.id}" must have exactly four padas`);
  }

  const padas = canonical.padas.map((pada, index) => {
    const navamsha = pada.navamsha_rashi
      ? rashiByVedicName.get(normalizeVedicName(pada.navamsha_rashi))
      : undefined;
    if (!navamsha) {
      throw new Error(`Unknown navamsha rashi for ${canonical.id} pada ${pada.pada}`);
    }
    return {
      navamsha: navamsha.name_en,
      traits: presentation.padaTraits[index],
    };
  });

  return {
    id: canonical.id,
    name_en: canonical.name_en,
    name_hi: canonical.name_hi,
    number: canonical.number,
    degree_start: canonical.degree_start,
    degree_end: canonical.degree_end,
    rashi: canonical.rashi,
    rashi_en: rashi.name_en,
    ruler: ruler.id,
    deity: canonical.deity,
    symbol: canonical.symbol,
    nature: requireLabel(NATURE_LABELS, canonical.nature),
    gana: titleCase(canonical.gana),
    quality: requireLabel(QUALITY_LABELS, canonical.quality),
    signifies: significationLabels(canonical.signifies),
    seo_text: presentation.seoText,
    padas,
  };
}

export const PLANETS: Readonly<Record<string, PlanetData>> = Object.fromEntries(
  planets.map((planet) => [planet.id, toPlanetData(planet)])
);

export const RASHI_NAMES: Readonly<Record<string, { en: string; hi: string }>> = Object.fromEntries(
  rashis.map((rashi) => [rashi.id, { en: rashi.name_en, hi: rashi.name_hi }])
);

export function formatDegree(degree: number): string {
  const signIndex = Math.floor(degree / 30) % rashis.length;
  const sign = [...rashis].sort((left, right) => left.number - right.number)[signIndex];
  const wholeDegrees = Math.floor(degree % 30);
  const minutes = Math.floor(((degree % 30) - wholeDegrees) * 60);
  return `${wholeDegrees}\u00b0${minutes}' ${sign.name_en}`;
}

export function ordinal(value: number): string {
  const suffixes = ["th", "st", "nd", "rd"];
  const remainder = value % 100;
  return value + (suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0]);
}

export function capitalize(value: string): string {
  return titleCase(value);
}

export const NAKSHATRA_DATA: NakshatraData[] = nakshatras.map(toNakshatraData);
